import { useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Button,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDate, fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedAdvancedPayments } from 'src/redux/slices/advanced-payments';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import CopyableDetail from 'src/components/copyable-detail';
import Lightbox, { useLightBox } from 'src/components/lightbox';
import TableErrorData from 'src/components/table/table-error-data';
import AuthenticatedImage from 'src/components/authenticated-image/authenticated-image';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { IEmployeeItem } from 'src/types/employee';
import { IAdvancedPaymentListItem } from 'src/types/advanced-payment';

import { paths } from '../../routes/paths';
import EmployeeActivityLog from './employee-activity-log';
import EndEmploymentDialog from './end-employment-dialog';
import EmployeeLoans from '../loan-agreement/employee-loans';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  {
    id: 'date',
    label: 'Paid Date',
    align: 'left',
    primary: true,
  },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'updatedAt', label: 'Updated At', primary: true, align: 'left' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'advancedPayments', label: 'Advanced Payments History' },
  { value: 'loans', label: 'Loans' },
];

type Props = {
  currentEmployee?: IEmployeeItem;
};

const defaultSortBy: { sortBy: string; order: 'asc' | 'desc' } = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function EmployeeDetails({ currentEmployee }: Props) {
  const dispatch = useAppDispatcher();

  const { user: authUser } = useAuthContext();

  const { status, payments, error } = useAppSelector((state) => state.advancedPayments);

  const [tabValue, setTabValue] = useState('overview');

  const endEmploymentDialog = useBoolean();

  const isEmploymentEnded = currentEmployee?.status === 'disabled';

  // The Loans tab calls a PermissionGuard-protected endpoint; a 403 would
  // redirect the whole page, so hide the tab instead.
  const canReadLoans = hasPermission(
    authUser,
    'any',
    AuthPermissions.READ_LOAN_AGREEMENT,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const { page, rowsPerPage } = table;

  const [sort, setSort] = useState(defaultSortBy);

  const [nicFrontImageUrl, setNicFrontImageUrl] = useState<string | null>(null);
  const [nicBackImageUrl, setNicBackImageUrl] = useState<string | null>(null);

  const nicSlides = [nicFrontImageUrl, nicBackImageUrl]
    .filter((url): url is string => !!url)
    .map((src) => ({ src }));

  const nicLightbox = useLightBox(nicSlides);

  useEffect(() => {
    if (tabValue === 'advancedPayments') {
      dispatch(
        getPaginatedAdvancedPayments({
          page,
          limit: rowsPerPage,
          sort: sort.order,
          sortBy: sort.sortBy,
          generalSearch: '',
          employee: currentEmployee?._id,
        })
      );
    }
  }, [tabValue, currentEmployee, dispatch, page, rowsPerPage, sort]);

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, value) => setTabValue(value)}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {TABS.filter((tab) => tab.value !== 'loans' || canReadLoans).map((tab) => (
              <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {tabValue === 'overview' && (
            <Grid container spacing={2} mt={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Stack direction="row" gap={1.5} alignItems="center">
                  <Typography variant="subtitle1">Basic Information</Typography>

                  <Label color={isEmploymentEnded ? 'error' : 'success'}>
                    {isEmploymentEnded ? 'Employment Ended' : 'Active'}
                  </Label>
                </Stack>

                <Stack gap={2} direction="row" alignItems="center">
                  {hasPermission(
                    authUser,
                    'any',
                    AuthPermissions.UPDATE_EMPLOYEE,
                    AuthPermissions.ADMIN_PERMISSION,
                    AuthPermissions.SUPER_ADMIN_PERMISSION
                  ) && (
                    <Link
                      component={RouterLink}
                      href={paths.dashboard.employees.edit(currentEmployee?._id ?? '')}
                    >
                      <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                        Edit
                      </Button>
                    </Link>
                  )}

                  {!isEmploymentEnded &&
                    hasPermission(
                      authUser,
                      'any',
                      AuthPermissions.END_EMPLOYMENT,
                      AuthPermissions.ADMIN_PERMISSION,
                      AuthPermissions.SUPER_ADMIN_PERMISSION
                    ) && (
                      <Button
                        variant="text"
                        color="error"
                        startIcon={<Iconify icon="solar:user-cross-bold" />}
                        onClick={endEmploymentDialog.onTrue}
                      >
                        End Employment
                      </Button>
                    )}
                </Stack>
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail
                  label="Employee Name"
                  value={`${currentEmployee?.firstName ?? ''}${
                    currentEmployee?.lastName ? ` ${currentEmployee.lastName}` : ''
                  }`}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail label="Employee Number" value={currentEmployee?.employeeNumber} />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail
                  label="Base Salary"
                  value={fCurrencyRupees((currentEmployee?.baseSalary || 0) * 100)}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail label="Deducting EPF" value={currentEmployee?.epf ? 'Yes' : 'No'} />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail label="NIC Number" value={currentEmployee?.nicNumber} />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail label="Gender" value={currentEmployee?.gender} capitalize />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail
                  label="Birthdate"
                  value={currentEmployee?.birthDate ? fDate(currentEmployee.birthDate) : '-'}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail
                  label="Joined At"
                  value={currentEmployee?.joinedAt ? fDate(currentEmployee.joinedAt) : '-'}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail
                  label="Created At"
                  value={currentEmployee?.createdAt ? fDateTime(currentEmployee.createdAt) : '-'}
                />
              </Grid>

              {isEmploymentEnded && (
                <>
                  <Grid xs={12} md={4}>
                    <CopyableDetail
                      label="Employment End Date"
                      value={
                        currentEmployee?.employmentEndAt
                          ? fDate(currentEmployee.employmentEndAt)
                          : '-'
                      }
                    />
                  </Grid>

                  <Grid xs={12} md={4}>
                    <CopyableDetail
                      label="Employment End Reason"
                      value={currentEmployee?.employmentEndReason ?? '-'}
                    />
                  </Grid>
                </>
              )}

              <Grid xs={12} md={4}>
                <CopyableDetail label="Phone Number" value={currentEmployee?.phoneNumber} />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail
                  label="Contact Person Name"
                  value={currentEmployee?.contactPersonName}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail
                  label="Contact Person Phone Number"
                  value={currentEmployee?.contactPersonPhoneNumber}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <CopyableDetail label="Address" value={currentEmployee?.address} />
              </Grid>

              {!!(
                currentEmployee?.bankDetails?.accountName ||
                currentEmployee?.bankDetails?.accountNumber ||
                currentEmployee?.bankDetails?.bank
              ) && (
                <>
                  <Grid mb={1} mt={2} xs={12} width="full">
                    <Typography variant="subtitle1">Bank Details</Typography>
                  </Grid>

                  <Grid xs={12} md={4}>
                    <CopyableDetail
                      label="Account Name"
                      value={currentEmployee?.bankDetails?.accountName}
                    />
                  </Grid>

                  <Grid xs={12} md={4}>
                    <CopyableDetail
                      label="Account Number"
                      value={currentEmployee?.bankDetails?.accountNumber}
                    />
                  </Grid>

                  <Grid xs={12} md={4}>
                    <CopyableDetail label="Bank" value={currentEmployee?.bankDetails?.bank} />
                  </Grid>
                </>
              )}

              <Grid mb={1} mt={2} xs={12} width="full">
                <Typography variant="subtitle1">NIC Images</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  NIC Front
                </Typography>
                {currentEmployee?.nicFrontImage?.key ? (
                  <AuthenticatedImage
                    src={`employee/${currentEmployee._id}/nic-image/front`}
                    alt="NIC Front"
                    sx={{ width: 240, height: 160, objectFit: 'cover' }}
                    onLoaded={setNicFrontImageUrl}
                    onClick={() => nicFrontImageUrl && nicLightbox.onOpen(nicFrontImageUrl)}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                    No image uploaded
                  </Typography>
                )}
              </Grid>

              <Grid xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  NIC Back
                </Typography>
                {currentEmployee?.nicBackImage?.key ? (
                  <AuthenticatedImage
                    src={`employee/${currentEmployee._id}/nic-image/back`}
                    alt="NIC Back"
                    sx={{ width: 240, height: 160, objectFit: 'cover' }}
                    onLoaded={setNicBackImageUrl}
                    onClick={() => nicBackImageUrl && nicLightbox.onOpen(nicBackImageUrl)}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                    No image uploaded
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}

          {tabValue === 'advancedPayments' && (
            <Stack>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {currentEmployee?.firstName}
                {currentEmployee?.lastName ? ` ${currentEmployee.lastName}` : ''}
              </Typography>
              <TableContainer
                sx={{ position: 'relative', overflow: 'hidden', height: '400px', mt: 2 }}
              >
                <Scrollbar>
                  <Table stickyHeader size="medium" sx={{ minWidth: 960 }}>
                    <TableHeadCustom
                      headLabel={LINE_ITEM_HEADERS}
                      order={sort.order}
                      orderBy={sort.sortBy}
                      rowCount={payments.items.length}
                      onSort={(id) => {
                        if (id === sort.sortBy) {
                          setSort((prev) => ({
                            ...prev,
                            order: prev.order === 'asc' ? 'desc' : 'asc',
                          }));
                        } else {
                          setSort({
                            sortBy: id,
                            order: 'asc',
                          });
                        }
                      }}
                    />

                    <TableBody>
                      {status === 'loading' ? (
                        [...Array(10)].map((i, index) => <TableSkeleton key={index} />)
                      ) : (
                        <>
                          {payments?.items.map((row: IAdvancedPaymentListItem, index: number) => (
                            <TableRow hover key={row._id}>
                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {fDateTime(row.paidDate)}
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {fCurrencyRupees(row.amountInCents)}
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {fDateTime(row.updatedAt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}

                      {status !== 'failure' && payments.count === 0 && (
                        <TableNoData message="No payment found" notFound={payments.count === 0} />
                      )}

                      {status === 'failure' && <TableErrorData notFound error={error} />}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
              <TablePaginationCustom
                count={payments.count}
                page={table.page}
                rowsPerPage={table.rowsPerPage}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </Stack>
          )}

          {tabValue === 'loans' && currentEmployee && (
            <EmployeeLoans employeeId={currentEmployee._id} />
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <Link component={RouterLink} href={paths.dashboard.employees.root}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_ADVANCED_PAYMENT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Link
                component={RouterLink}
                href={paths.dashboard.advancedPayments.new(currentEmployee?._id ?? '')}
              >
                <Button variant="contained" color="inherit">
                  Make Payment
                </Button>
              </Link>
            )}
          </Stack>
        </Card>
      </Grid>

      {currentEmployee &&
        hasPermission(
          authUser,
          'any',
          AuthPermissions.READ_AUDIT,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <Grid xs={12}>
            <EmployeeActivityLog employeeId={currentEmployee._id} />
          </Grid>
        )}

      {currentEmployee && endEmploymentDialog.value && (
        <EndEmploymentDialog
          employee={currentEmployee}
          isOpen={endEmploymentDialog.value}
          onClose={endEmploymentDialog.onFalse}
          onSuccess={endEmploymentDialog.onFalse}
        />
      )}

      <Lightbox
        index={nicLightbox.selected}
        slides={nicSlides}
        open={nicLightbox.open}
        close={nicLightbox.onClose}
      />
    </Grid>
  );
}
